import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCapIcon } from 'lucide-react';
import { findDegreesTemplates } from '../data/findDegreesTemplates';
import { siteTemplates } from '../data/siteIndex';
import { FooterTemplateDropdown } from './FooterTemplateDropdown';
function slugToLabel(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * The templates worth listing inline in the footer. The degree-program
 * template runs to several hundred URLs and the theme pages have their own
 * section, so both are reached through their index pages instead.
 */
const footerTemplateIds = ['guides', 'program-information', 'degree-categories', 'colleges'];
const routeTemplates = siteTemplates.filter(template => footerTemplateIds.includes(template.id)).map(template => ({
  label: template.label,
  pattern: template.pattern,
  links: [...(template.indexTo ? [{
    label: `All ${template.label}`,
    to: template.indexTo
  }] : []), ...template.pages.map(page => ({
    label: page.label,
    to: page.to
  }))]
}));
export function Footer() {
  const currentYear = new Date().getFullYear();
  const scrollToTop = () => window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  return <footer className="bg-navy-dark text-white pt-16 pb-8" data-id="element-226" data-mp="3q725cclzmq8m">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227" data-mp="82id3u6tecfd">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12" data-id="element-228" data-mp="80xmgfnmfdms">
          {/* Brand Col */}
          <div className="col-span-1 md:col-span-1" data-id="element-229" data-mp="32339clqrn25">
            <Link to="/" onClick={scrollToTop} className="flex items-center mb-4" data-id="element-230" data-mp="hrs7xht1m976">
              <GraduationCapIcon className="h-8 w-8 text-gold-light mr-2" data-id="element-231" data-mp="cvtng5au5bc1" />
              <span className="text-xl font-bold text-white tracking-tight" data-id="element-232" data-mp="qu1fpzwxfogv">
                Online College Guide
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-233" data-mp="tvd0bu5d69g7">
              Empowering students to find the perfect online degree program to
              advance their careers and achieve their goals.
            </p>
          </div>

          {/* Guides Col — every URL each route template serves */}
          <div data-id="element-234" data-mp="18kitb0dlz3fn">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-235" data-mp="18l2d6mmtk1wb">
              Guides
            </h3>
            <div className="space-y-2" data-id="element-236" data-mp="1clcmo99tlafx">
              {routeTemplates.map(template => <FooterTemplateDropdown key={template.pattern} label={template.label} pattern={template.pattern} links={template.links} data-id="element-237" data-mp="1hiujj1akbz7k" />)}
            </div>
          </div>

          {/* Site Links Col */}
          <div data-id="element-238" data-mp="3oczspkkjszas">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-239" data-mp="3odhvl01pubro">
              Get Started
            </h3>
            <ul className="space-y-3 mb-6" data-id="element-240" data-mp="2afdidco5p8os">
              <li data-id="element-241" data-mp="2agey3ug4ajcu">
                <Link to="/request-information" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-242" data-mp="2agybcxutojkj">
                  Request Information
                </Link>
              </li>
              <li data-id="element-243" data-mp="1wlvzlu3anzzt">
                <Link to="/get-matched" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-244" data-mp="2tdptv52jiunj">
                  Find My Match
                </Link>
              </li>
            </ul>

            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-245" data-mp="2fe6x18f6j5qj">
              Find Online Degrees
            </h3>
            <ul className="space-y-3 mb-6" data-id="element-246" data-mp="18z63189psxml">
              <li data-id="element-247" data-mp="18vrxxs9o2abg">
                <Link to="/find-degrees" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-248" data-mp="13zb72qb31huo">
                  Search All Programs
                </Link>
              </li>
              {findDegreesTemplates.map(template => <li key={template.slug} data-id="element-249" data-mp="2iwcg4dbas9ts">
                  <Link to={`/find-degrees/${template.slug}`} onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-250" data-mp="2nu5nh9oc79vn">
                    {slugToLabel(template.slug)}
                  </Link>
                </li>)}
            </ul>

            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-251" data-mp="31l10my5vcd51">
              Resources
            </h3>
            <ul className="space-y-3" data-id="element-252" data-mp="36mgnmvrlwtq2">
              <li data-id="element-253" data-mp="1q647pd6kd3se">
                <Link to="/articles" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-254" data-mp="1q5m6s38yi1e5">
                  Articles
                </Link>
              </li>
              <li data-id="element-255" data-mp="1ine8gehgup6t">
                <Link to="/colleges" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-256" data-mp="1iltdwov5j68y">
                  Partner Colleges
                </Link>
              </li>
              <li data-id="element-257" data-mp="13xgo69mnqjmw">
                <Link to="/pages" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-258" data-mp="13z04u1c86wp7">
                  Browse All Pages
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Col */}
          <div data-id="element-259" data-mp="18vqi6gwnbe6x">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-260" data-mp="18v8fb1fha1q1">
              Legal
            </h3>
            <ul className="space-y-3" data-id="element-261" data-mp="g8xj02ywmmz1">
              <li data-id="element-262" data-mp="g7uozm6ac4ew">
                <Link to="/privacy-policy" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-263" data-mp="g6b9vywd7zep">
                  Privacy Policy
                </Link>
              </li>
              <li data-id="element-264" data-mp="2q0ym0geajakc">
                <Link to="/terms-and-conditions" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-265" data-mp="2q2jhc88fixj9">
                  Terms of Service
                </Link>
              </li>
              <li data-id="element-266" data-mp="22b4ac25pjs1o">
                <Link to="/cookie-policy" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-267" data-mp="22cp5ntzujf0l">
                  Cookie Policy
                </Link>
              </li>
              <li data-id="element-268" data-mp="3ioufzdch6odf">
                <Link to="/do-not-sell-my-info" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-269" data-mp="3irgslre83u2i">
                  Do Not Sell My Info
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8" data-id="element-270" data-mp="12a8xxhxn212r">
          <div className="text-xs text-gray-400 space-y-4 text-center md:text-left" data-id="element-271" data-mp="128phnrbzfq0z">
            <p data-id="element-272" data-mp="x9v1kx5egfkk">
              <strong data-id="element-273" data-mp="xa4q0gasqeo5">Advertiser Disclosure:</strong> OnlineCollegeGuide.com is
              an advertising-supported site. Featured or trusted partner
              programs and all school search, finder, or match results are for
              schools that compensate us. This compensation does not influence
              our school rankings, resource guides, or other
              editorially-independent information published on this site.
            </p>
            <p data-id="element-274" data-mp="2dow7vzabg3j7">
              <strong data-id="element-275" data-mp="2dpfrrljj11zv">TCPA Disclosure:</strong> By submitting your information
              on this website, you consent to be contacted by our educational
              partners regarding their programs via email, telephone, or text
              message.
            </p>
            <p className="pt-4 text-gray-500" data-id="element-276" data-mp="1mjjw5eyhu6e8">
              &copy; {currentYear} OnlineCollegeGuide. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>;
}